/**
 * Cheap pre-upload video probe using a hidden <video> element.
 *
 * Returns only what the browser can give reliably without WASM/codec libs:
 * width, height, duration. Codec/fps/audio info is NOT available here — those
 * still come from the authoritative server probe (/services/probe-video).
 *
 * Throws on any of: metadata never fires within METADATA_TIMEOUT_MS, duration
 * is Infinity (e.g. MOV without fast-start moov atom), or dimensions are 0.
 * The caller is expected to catch and silently fall through to the BE probe.
 */

export interface BrowserVideoMeta {
    width: number;
    height: number;
    duration: number;
}

const METADATA_TIMEOUT_MS = 5000;

export const browserVideoProbe = (file: File): Promise<BrowserVideoMeta> => {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement("video");
        video.preload = "metadata";
        video.muted = true;
        video.playsInline = true;

        let settled = false;
        const cleanup = (): void => {
            video.onloadedmetadata = null;
            video.onerror = null;
            URL.revokeObjectURL(url);
        };
        const finish = (cb: () => void): void => {
            if (settled) return;
            settled = true;
            clearTimeout(timeout);
            cleanup();
            cb();
        };

        const timeout = setTimeout(() => {
            finish(() => reject(new Error("Metadata load timed out")));
        }, METADATA_TIMEOUT_MS);

        video.onloadedmetadata = (): void => {
            const { videoWidth, videoHeight, duration } = video;
            if (!videoWidth || !videoHeight) {
                finish(() => reject(new Error("Video dimensions unavailable")));
                return;
            }
            if (!Number.isFinite(duration) || duration <= 0) {
                finish(() => reject(new Error("Video duration unavailable")));
                return;
            }
            finish(() =>
                resolve({ width: videoWidth, height: videoHeight, duration })
            );
        };

        video.onerror = (): void => {
            finish(() => reject(new Error("Video metadata load failed")));
        };

        video.src = url;
    });
};
